import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { MenuIcon, X, ChevronDownIcon, ShieldCheck, Activity, Smile, Heart, User, Phone, Package, Gift, RefreshCw, Cross } from 'lucide-react';
import { GoogleTagManager } from '../utils/GoogleTagManager';
import { MetaPixel } from '../utils/MetaPixel';
// --- ANIMATION COMPONENT ---
// Wraps content to create a smooth "Fade Up" effect when scrolling
function Reveal({
  children,
  delay = 0,
  className = '',
  width = 'w-full'
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);
  return <div ref={ref} className={`transition-all duration-1000 ease-out transform ${width} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${className}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-2">
      {children}
    </div>;
}
// --- HELPER COMPONENTS ---
function NavLink({
  href,
  label
}) {
  return <a href={href} className="relative group text-white hover:text-white/90 transition-colors text-xs xl:text-sm font-bold tracking-widest uppercase py-2" data-id="element-3">
      {label}
      <span className="absolute -bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full opacity-0 group-hover:opacity-100" data-id="element-4"></span>
    </a>;
}
function DropdownItem({
  href,
  title,
  desc,
  icon: Icon
}) {
  return <a href={href} className="flex items-center gap-4 px-3 py-2 rounded-lg hover:bg-[#F5F3EE] transition-colors group" data-id="element-5">
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-[#496f58]/10 text-[#496f58] group-hover:bg-[#496f58] group-hover:text-white transition-colors" data-id="element-6">
        <Icon size={18} strokeWidth={1.5} data-id="element-7" />
      </div>
      <div data-id="element-8">
        <p className="text-sm font-semibold text-[#1A2332] group-hover:text-[#496f58] transition-colors" data-id="element-9">
          {title}
        </p>
        <p className="text-xs text-gray-500 font-medium" data-id="element-10">{desc}</p>
      </div>
    </a>;
}
function MobileLink({
  href,
  label
}) {
  return <a href={href} className="block text-white/80 hover:text-white transition-colors text-base font-medium" data-id="element-11">
      {label}
    </a>;
}
// --- NAVBAR COMPONENT ---
export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBenefitOpen, setIsBenefitOpen] = useState(false);
  const [isMobileBenefitOpen, setlsMobileBenefitOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  // Slide down animation on mount
  useEffect(() => {
    setIsMounted(true);
  }, []);
  return <nav className={`bg-[#496f58] fixed top-0 w-full z-50 shadow-sm font-sans border-b border-white/10 h-[70px] transition-transform duration-700 ease-out ${isMounted ? 'translate-y-0' : '-translate-y-full'}`} data-id="element-12">
      <div className="w-full px-6 lg:px-12 h-full flex items-center" data-id="element-13">
        <div className="max-w-7xl mx-auto w-full flex justify-between items-center" data-id="element-14">
          {/* Left Navigation - Main Links */}
          <div className="hidden lg:flex items-center gap-8 flex-1" data-id="element-15">
            <NavLink href="https://supplement.faithandfig.com" label="Home" data-id="element-16" />

            {/* Shop by Benefit with hover dropdown */}
            <div className="relative group h-full flex items-center" onMouseEnter={() => setIsBenefitOpen(true)} onMouseLeave={() => setIsBenefitOpen(false)} data-id="element-17">
              <button className={`flex items-center gap-1.5 transition-colors text-xs xl:text-sm font-bold tracking-widest uppercase py-4 ${isBenefitOpen ? 'text-white/80' : 'text-white'}`} data-id="element-18">
                Shop By Benefit
                <ChevronDownIcon size={14} className={`transition-transform duration-200 ${isBenefitOpen ? 'rotate-180' : ''}`} data-id="element-19" />
              </button>
              {/* Dropdown Container */}
              <div className={`absolute left-0 top-full w-72 pt-2 transform transition-all duration-200 ease-in-out origin-top-left ${isBenefitOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 translate-y-2 invisible'}`} data-id="element-20">
                <div className="bg-white rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden" data-id="element-21">
                  <div className="p-2 space-y-1" data-id="element-22">
                    <DropdownItem href="https://oliveleaf.faithandfig.com" title="Immune Defense" desc="Olive Leaf Extract" icon={ShieldCheck} data-id="element-23" />
                    <DropdownItem href="https://frankincense.faithandfig.com" title="Joint Comfort" desc="Frankincense & Boswellia" icon={Activity} data-id="element-24" />
                    <DropdownItem href="https://trysaffron.faithandfig.com" title="Mood & Focus" desc="Premium Saffron" icon={Smile} data-id="element-25" />
                    <DropdownItem href="https://pomegranates.faithandfig.com" title="Heart & Vitality" desc="Pomegranate Extract" icon={Heart} data-id="element-26" />
                  </div>
                </div>
              </div>
            </div>
            <NavLink href="https://learnmore.faithandfig.com" label="About Us" data-id="element-27" />
            <NavLink href="https://faithandfig.com/pages/contact" label="Contact" data-id="element-28" />
          </div>

          {/* Center Logo */}
          <div className="flex items-center justify-start lg:justify-center flex-shrink-0 mx-4" data-id="element-29">
            <a href="https://supplement.faithandfig.com" className="flex items-center hover:opacity-90 transition-opacity" data-id="element-30">
              <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/1000007566.jpg?v=1763689502" alt="F&F Logo" className="h-7 md:h-7 w-auto y-centered" data-id="element-31" />
            </a>
          </div>

          {/* Right Navigation - Utilities & Actions */}
          <div className="hidden lg:flex items-center justify-end gap-6 flex-1" data-id="element-32">
            <div className="flex items-center gap-6 border-r border-white/20 pr-6 mr-2" data-id="element-33">
              <a href="https://faithandfig.com/apps/my-subscriptions" className="group flex items-center gap-2 text-white/90 hover:text-white transition-colors" title="Manage Subscription" data-id="element-34">
                <RefreshCw size={16} className="opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-35" />
                <span className="text-xs font-medium tracking-wide" data-id="element-36">
                  Subscriptions
                </span>
              </a>
              <a href="https://faithandfig.com/apps/track123" className="group flex items-center gap-2 text-white/90 hover:text-white transition-colors" title="Track Your Order" data-id="element-37">
                <Package size={16} className="opacity-70 group-hover:opacity-100 transition-opacity" data-id="element-38" />
                <span className="text-xs font-medium tracking-wide" data-id="element-39">Track</span>
              </a>
            </div>
            <a href="https://faithandfig.com" className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white text-white hover:text-[#496f58] rounded-full transition-all duration-300 backdrop-blur-sm border border-white/20 hover:border-white group shadow-sm hover:shadow-md" data-id="element-40">
              <Gift size={16} className="group-hover:scale-110 transition-transform duration-300" data-id="element-41" />
              <span className="text-xs font-bold tracking-wider uppercase" data-id="element-42">
                Gift Store
              </span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex justify-end flex-1" data-id="element-43">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white hover:text-white/80 transition-colors p-1" data-id="element-44">
              {isMenuOpen ? <X size={28} data-id="element-45" /> : <MenuIcon size={28} data-id="element-46" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <div className="lg:hidden w-full bg-[#496f58] border-t border-white/10 h-[calc(100vh-70px)] overflow-y-auto absolute top-[70px] left-0 animate-in slide-in-from-top-10 duration-300" data-id="element-47">
          <div className="px-6 py-6 flex flex-col min-h-full" data-id="element-48">
            {/* Section 1: Main Navigation */}
            <div className="space-y-4 mb-8" data-id="element-49">
              <p className="text-xs font-bold text-white/40 tracking-widest uppercase" data-id="element-50">
                Menu
              </p>
              <a href="https://supplement.faithandfig.com" className="block text-white text-xl font-medium" data-id="element-51">
                HOME
              </a>
              {/* Mobile Shop by Benefit */}
              <div className="space-y-3" data-id="element-52">
                <button onClick={() => setlsMobileBenefitOpen(!isMobileBenefitOpen)} className="flex w-full items-center justify-between text-white text-xl font-medium" data-id="element-53">
                  <span data-id="element-54">SHOP BY BENEFIT</span>
                  <ChevronDownIcon size={20} className={`transition-transform duration-300 ${isMobileBenefitOpen ? 'rotate-180' : ''}`} data-id="element-55" />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isMobileBenefitOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-56">
                  <div className="ml-2 pl-4 border-l-2 border-white/20 space-y-3 py-2" data-id="element-57">
                    <MobileLink href="https://oliveleaf.faithandfig.com" label="Immune Defense" data-id="element-58" />
                    <MobileLink href="https://frankincense.faithandfig.com" label="Joint Comfort" data-id="element-59" />
                    <MobileLink href="https://trysaffron.faithandfig.com" label="Mood & Focus" data-id="element-60" />
                    <MobileLink href="https://pomegranates.faithandfig.com" label="Heart & Vitality" data-id="element-61" />
                  </div>
                </div>
              </div>
              <a href="https://learnmore.faithandfig.com" className="block text-white text-xl font-medium" data-id="element-62">
                ABOUT US
              </a>
            </div>

            {/* Section 2: Customer Care */}
            <div className="space-y-4 mb-8 border-t border-white/10 pt-6" data-id="element-63">
              <p className="text-xs font-bold text-white/40 tracking-widest uppercase" data-id="element-64">
                Customer Care
              </p>
              <a href="https://faithandfig.com/pages/contact" className="flex items-center gap-3 text-white/90 hover:text-white transition-colors" data-id="element-65">
                <Phone size={18} data-id="element-66" />
                <span className="font-medium" data-id="element-67">Contact Us</span>
              </a>
              <a href="https://faithandfig.com/apps/my-subscriptions" className="flex items-center gap-3 text-white/90 hover:text-white transition-colors" data-id="element-68">
                <User size={18} data-id="element-69" />
                <span className="font-medium" data-id="element-70">Manage Subscription</span>
              </a>
              <a href="https://faithandfig.com/apps/track123" className="flex items-center gap-3 text-white/90 hover:text-white transition-colors" data-id="element-71">
                <Package size={18} data-id="element-72" />
                <span className="font-medium" data-id="element-73">Track Your Order</span>
              </a>
            </div>

            {/* Section 3: Highlight Actions */}
            <div className="mt-auto pb-8" data-id="element-74">
              <a href="https://faithandfig.com" className="flex items-center justify-center gap-2 w-full px-6 py-4 bg-white text-[#496f58] rounded-xl font-bold shadow-lg hover:bg-gray-100 transition-colors" data-id="element-75">
                <Gift size={20} data-id="element-76" />
                VISIT GIFT STORE
              </a>
              <p className="text-center text-white/30 text-xs mt-6" data-id="element-77">
                ©2025 Faith & Fig. All rights reserved.
              </p>
            </div>
          </div>
        </div>}
    </nav>;
}
// --- HERO COMPONENT ---
export function FaithAndFigCampaign() {
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const words = ['CALM', 'FOCUS', 'BALANCE', 'PEACE', 'FAITH', 'SLEEP'];
  useEffect(() => {
    setLoaded(true);
    const interval = setInterval(() => {
      setIndex(prevIndex => (prevIndex + 1) % words.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);
  return <>
      <style data-id="element-78">{`
        @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400&display=swap');
        :root { --color-primary: #496f58; --color-cream: #F5F1EB; --color-gold: #C9A962; }
        body { margin: 0; padding: 0; background-color: var(--color-primary); }
        .font-serif-brand { font-family: 'Playfair Display', serif; }
        .font-sans-brand { font-family: 'Lato', sans-serif; }
        .fade-text { transition: opacity 1s ease-in-out, transform 1s ease-in-out; }
        /* Initial Hero Animations */
        .hero-fade-in { opacity: 0; transform: translateY(20px); animation: heroFadeIn 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards; }
        @keyframes heroFadeIn { to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div className="w-full text-[#F5F1EB] overflow-hidden flex flex-col justify-center relative selection:bg-[#C9A962] selection:text-[#496f58] min-h-[75vh] sm:min-h-[60vh] lg:min-h-[60vh]" data-id="element-79">
        <div className="relative z-10 px-2 md:px-12 lg:px-20 py-2 md:py-20 max-w-[1920px] mx-auto w-full flex flex-col justify-center" data-id="element-80">
          <h1 className="sr-only" data-id="element-81">
            We restore calm, focus, balance, and peace. Faith & Fig.
          </h1>

          {/* Verse - Animated on Load */}
          <div className={`w-full flex flex-col items-center justify-center text-center mb-8 md:mb-10 transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{
          transitionDelay: '100ms'
        }} data-id="element-82">
            <p className="font-serif-brand text-sm md:text-xl lg:text-xl text-[#F5F1EB] italic tracking-wide leading-relaxed opacity-90 max-w-3xl" data-id="element-83">
              "And God said, 'Behold, I have given you every herb bearing seed…
              and every tree, in which is the fruit of a tree yielding seed; to
              you it shall be for food."
            </p>
            <p className="font-sans-brand text-xs md:text-xs text-[#C9A962] mt-3 tracking-[0.25em] uppercase font-bold opacity-80" data-id="element-84">
              Genesis 1:29
            </p>
          </div>

          {/* Manifesto - Staggered Animation */}
          <div className="font-serif-brand leading-[0.85] tracking-tight mt-8 md:mt-0" data-id="element-85">
            <div className="text-[17vw] md:text-[16vw] lg:text-[10vw] font-normal block hero-fade-in" style={{
            animationDelay: '300ms'
          }} data-id="element-86">
              WE
            </div>
            <div className="text-[17vw] md:text-[16vw] lg:text-[10vw] font-normal block hero-fade-in" style={{
            animationDelay: '500ms'
          }} data-id="element-87">
              RESTORE
            </div>
            <div className="relative h-[20vw] md:h-[17vw] lg:h-[12vw] w-full hero-fade-in" style={{
            animationDelay: '700ms'
          }} data-id="element-88">
              {words.map((word, i) => <div key={word} className={`absolute top-0 left-0 whitespace-nowrap text-[19vw] md:text-[16vw] lg:text-[14vw] font-semibold italic text-[#C9A962] fade-text ${i === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} aria-hidden={i !== index} data-id="element-89">
                  {word}
                </div>)}
              <span className="invisible text-[19vw] md:text-[16vw] lg:text-[14vw]" data-id="element-90">
                PLACEHOLDER
              </span>
            </div>
          </div>

          {/* Footnote */}
          <div className={`mt-8 border-t border-[#F5F1EB]/30 pt-8 w-full flex justify-center transition-all duration-1000 delay-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`} data-id="element-91">
            <div className="max-w-4xl px-4" data-id="element-92">
              <p className="text-sm font-sans-brand text-[#E8DFD4] font-light italic leading-relaxed tracking-wide text-center" data-id="element-93">
                We believe that God has provided every plant, herb, and seed to
                care for the human body.
                <br className=" md:block" data-id="element-94" />
                <br className=" md:block" data-id="element-95" />
                We honor these botanicals by combining them with modern
                research, bringing thoughtful, creation-based support to the
                body He entrusted to us.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}
// --- IMAGE WITH TEXT SLIDESHOW ---
function ImageTextSlideshow() {
  const scrollContainerRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const handleMouseDown = e => {
    setIsDown(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };
  const handleMouseLeave = () => setIsDown(false);
  const handleMouseUp = () => setIsDown(false);
  const handleMouseMove = e => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };
  const baseSlides = [{
    id: 1,
    imageSrc: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_6_Category-03_Destop.jpg?v=1764727727',
    mobileImageSrc: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_7_Category-03_Mobile.jpg?v=1764746253',
    title: 'Results You Can Feel',
    content: <p className="font-sans-brand text-sm md:text-base text-white max-w-xl lg:max-w-2xl mb-6 md:mb-8 leading-relaxed font-normal" data-id="element-96">
          Dose is always potent. We use high concentrations of{' '}
          <strong className="font-bold" data-id="element-97">clinically proven ingredients</strong>.
          Dosages are informed by human clinical studies, traditional usage, and
          FDA recommended levels. When formulating, we work alongside scientists
          and herbalists to ensure{' '}
          <strong className="font-bold" data-id="element-98">efficacy, safety</strong>, and{' '}
          <strong className="font-bold" data-id="element-99">purity.</strong>
        </p>,
    linkText: 'WHY IT WORKS',
    linkHref: '#'
  }, {
    id: 2,
    imageSrc: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_8_Category-01_Destop.jpg?v=1764727727',
    mobileImageSrc: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_9_Category-01_Mobile.jpg?v=1764746253',
    title: 'Bioactive + Bioavailable',
    content: <div className="font-sans-brand text-white max-w-4xl mx-auto mb-2 md:mb-6" data-id="element-100">
          <p className="text-xs md:text-base mb-4 md:mb-6 leading-relaxed font-normal max-w-2xl mx-auto" data-id="element-101">
            Form is always easy for your body to absorb. Ingredients are{' '}
            <strong className="font-bold" data-id="element-102">bioactive</strong> (easily absorbed)
            and <strong className="font-bold" data-id="element-103">bioavailable</strong>.{' '}
            <strong className="font-bold" data-id="element-104">
              No flow agents that inhibit absorption.
            </strong>
          </p>
          <p className="text-center mb-2 md:mb-4 text-xs md:text-base font-medium" data-id="element-105">
            We never formulate with:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-2 md:gap-x-4 gap-y-1 text-[10px] md:text-xs text-left font-bold tracking-wide leading-tight mx-auto max-w-3xl" data-id="element-106">
            <div className="space-y-1 md:space-y-1.5" data-id="element-107">
              <div data-id="element-108">× Additives/Binders</div>
              <div data-id="element-109">× Animal derived</div>
              <div data-id="element-110">× Artificial colors/flavors</div>
              <div data-id="element-111">× Gelatin</div>
              <div data-id="element-112">× Gluten</div>
              <div data-id="element-113">× GMOs</div>
            </div>
            <div className="space-y-1 md:space-y-1.5" data-id="element-114">
              <div data-id="element-115">× Isomalt</div>
              <div data-id="element-116">× Magnesium stearate</div>
              <div data-id="element-117">× Maltitol</div>
              <div data-id="element-118">× Maltodextrin</div>
              <div data-id="element-119">× Modified ingredients</div>
              <div data-id="element-120">× MSG</div>
            </div>
            <div className="space-y-1 md:space-y-1.5 col-span-2 md:col-span-1 grid grid-cols-2 md:block gap-x-2" data-id="element-121">
              <div data-id="element-122">× Palm oil</div>
              <div data-id="element-123">× Soy</div>
              <div className="col-span-2" data-id="element-124">× Sugar/Syrup/Xylitol</div>
              <div data-id="element-125">× Titanium dioxide</div>
            </div>
          </div>
        </div>,
    linkText: 'OUR FORMULAS',
    linkHref: '#'
  }, {
    id: 3,
    imageSrc: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_10_Category-02_Destop.jpg?v=1764727727',
    mobileImageSrc: 'https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_11_Category-02_Mobile.jpg?v=1764746253',
    title: '100% Traceable',
    content: <p className="font-sans-brand text-sm md:text-base text-white max-w-xl lg:max-w-2xl mb-6 md:mb-8 leading-relaxed font-normal" data-id="element-126">
          Source is always transparent. We use{' '}
          <strong className="font-bold" data-id="element-127">pure, unadulterated</strong> ingredients
          that pass through multiple rounds of testing. We go above and beyond
          to <strong className="font-bold" data-id="element-128">3rd party test</strong> for
          bio-identity, purity, potency, and safety.
        </p>,
    linkText: 'OUR INGREDIENTS',
    linkHref: '#'
  }];
  const slides = [...baseSlides, ...baseSlides, ...baseSlides, ...baseSlides, ...baseSlides];
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
    }
  }, []);
  return <section className="bg-[#F5F1EB] py-6 md:py-8 overflow-hidden" data-id="element-129">
      <style data-id="element-130">{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <Reveal delay={200} data-id="element-131">
        <div ref={scrollContainerRef} onMouseDown={handleMouseDown} onMouseLeave={handleMouseLeave} onMouseUp={handleMouseUp} onMouseMove={handleMouseMove} className={`flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar px-6 md:px-12 lg:px-24 select-none ${isDown ? 'cursor-grabbing snap-none' : 'cursor-grab snap-x'} `} data-id="element-132">
          {slides.map((slide, i) => <div key={`${slide.id}-${i}`} className="relative flex-shrink-0 w-[85vw] md:w-[70vw] lg:w-[60vw] h-[550px] md:h-[600px] lg:h-[650px] snap-center rounded-lg overflow-hidden" data-id="element-133">
              <img src={slide.imageSrc} alt={slide.title} className="hidden md:block absolute inset-0 w-full h-full object-cover pointer-events-none" data-id="element-134" />
              <img src={slide.mobileImageSrc || slide.imageSrc} alt={slide.title} className="block md:hidden absolute inset-0 w-full h-full object-cover pointer-events-none" data-id="element-135" />
              <div className="absolute inset-0 bg-black/40 pointer-events-none" data-id="element-136" />
              <div className="absolute inset-0 flex flex-col items-center justify-center px-4 md:px-12 text-center z-20 overflow-y-auto scrollbar-hide" data-id="element-137">
                <div className="w-full h-full flex flex-col items-center justify-center py-8" data-id="element-138">
                  <h3 className="text-2xl md:text-4xl lg:text-5xl font-sans-brand font-bold text-white mb-4 md:mb-6 tracking-tight leading-tight shrink-0" data-id="element-139">
                    {slide.title}
                  </h3>
                  <div className="shrink-0 w-full" data-id="element-140">{slide.content}</div>
                  <a href={slide.linkHref} className="font-sans-brand text-xs md:text-sm font-bold text-white uppercase tracking-[0.25em] underline decoration-2 underline-offset-4 hover:text-white/90 transition-colors mt-auto md:mt-0 pt-4 shrink-0" data-id="element-141">
                    {slide.linkText}
                  </a>
                </div>
              </div>
            </div>)}
        </div>
      </Reveal>
    </section>;
}
// --- NOTE FROM FOUNDER COMPONENT (Upgraded) ---
function NoteFromFounder() {
  return <section className="relative py-24 md:py-32 overflow-hidden" data-id="element-142">
      {/* Gritty paper/warm light gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FFFBF5] to-[#F2EBE3] -z-10" data-id="element-143" />

      {/* Subtle decorative background element */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#C9A962] rounded-full mix-blend-multiply filter blur-[120px] opacity-5 pointer-events-none -z-10" data-id="element-144"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative" data-id="element-145">
        {/* Header with subtle ornament */}
        <Reveal data-id="element-146">
          <div className="text-center mb-12 md:mb-16" data-id="element-147">
            <Cross strokeWidth={1} size={24} className="text-[#C9A962] mx-auto mb-4 opacity-60" data-id="element-148" />
            <h2 className="text-4xl md:text-5xl font-serif-brand font-medium text-[#496f58] tracking-tight" data-id="element-149">
              A Note From Our Founder
            </h2>
          </div>
        </Reveal>

        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 max-w-6xl mx-auto" data-id="element-150">
          {/* Image Container (Left on Desktop) */}
          <div className="w-full lg:w-1/2 flex justify-center lg:justify-end relative" data-id="element-151">
            <Reveal delay={200} width="w-full max-w-md" data-id="element-152">
              <div className="relative" data-id="element-153">
                {/* Decorative offset framing element behind image */}
                <div className="absolute inset-0 bg-[#496f58] opacity-5 rounded-[2.5rem] transform translate-x-4 translate-y-4 md:translate-x-6 md:translate-y-6 -z-10" data-id="element-154"></div>

                {/* Main Image */}
                <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_13_Amanda_800x_4a33a47d-4a5c-42c9-bba0-39ab7bd31b57.jpg?v=1764748393" alt="Amanda, Founder of Faith & Fig" className="w-full rounded-[2rem] border-8 border-white shadow-2xl object-cover aspect-[3/4]" data-id="element-155" />
              </div>
            </Reveal>
          </div>

          {/* Text Container (Right on Desktop) */}
          <div className="w-full lg:w-1/2 flex flex-col items-start text-left" data-id="element-156">
            <Reveal delay={400} data-id="element-157">
              {/* Lead-in paragraph in Serif for emphasis */}
              <p className="font-serif-brand text-2xl md:text-3xl text-[#496f58] leading-relaxed mb-6 font-light" data-id="element-158">
                "Hi, I'm so glad you're here."
              </p>

              {/* Main Body Text */}
              <div className="font-sans-brand text-[#2F3E34]/80 leading-loose space-y-6 text-base md:text-lg" data-id="element-159">
                <p data-id="element-160">
                  Faith & Fig was born from a desire to reconcile my faith with
                  modern wellness. I realized that the oldest wisdom—the
                  botanical bounty mentioned throughout Scripture—often held the
                  clearest path to healing.
                </p>
                <p data-id="element-161">
                  I sought out experts, studied modern clinical research, and
                  combined it with the knowledge of God's creation to craft
                  formulas that are intentional, pure, and effective. Health is
                  not just physical; it is an act of worship. My mission is to
                  help you steward the one temple you have been given.
                </p>
              </div>

              {/* Signature Block */}
              <div className="mt-10 pt-8 border-t border-[#496f58]/10 w-full flex flex-col items-start" data-id="element-162">
                <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/imgi_14_Amanda-signature.png?v=1764748393" alt="Amanda's Signature" className="h-16 md:h-20 w-auto mb-4 -ml-2 opacity-90" data-id="element-163" />

                <div data-id="element-164">
                  <p className="font-serif-brand text-lg font-bold text-[#496f58] tracking-wide" data-id="element-165">
                    Amanda Messier
                  </p>
                  <p className="font-sans-brand text-sm text-[#C9A962] font-bold uppercase tracking-widest mt-1" data-id="element-166">
                    Founder & CEO
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}
// --- CONTENT SECTION GROUP ---
function AboutUsSections() {
  return <>
      {/* SECTION 2: ABOUT F&F (Layout-Aware Glassmorphism) */}
      <section className="relative w-full min-h-[800px] md:min-h-[700px] flex flex-col justify-start md:justify-center overflow-hidden" data-id="element-167">
        <div className="absolute inset-0 z-0 bg-[#F9F7F2]" data-id="element-168">
          <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/d7c0675b-b561-4d92-b240-196ab5d36252.png?v=1764716813" alt="Background" className="w-full h-full object-cover object-bottom md:hidden" data-id="element-169" />
          <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/8288bdaf-b93e-46f1-99e9-3e4993d04b55.png?v=1764716809" alt="Background" className="hidden md:block w-full h-full object-cover object-right" data-id="element-170" />
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-white/40 via-transparent to-transparent" data-id="element-171"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 h-full flex flex-col md:flex-row items-start md:items-center" data-id="element-172">
          <div className="w-full md:w-1/2 lg:w-5/12 mt-16 md:mt-0" data-id="element-173">
            <Reveal delay={100} data-id="element-174">
              <div className="mb-8 md:mb-10 text-center md:text-left" data-id="element-175">
                <p className="font-sans-brand text-xs font-bold tracking-[0.3em] text-[#C9A962] uppercase mb-4" data-id="element-176">
                  About F&F
                </p>
                <h2 className="text-3xl md:text-5xl font-serif-brand font-medium text-[#496f58] leading-[1.15]" data-id="element-177">
                  The first Christian supplement brand designed to honor God's
                  creation.
                </h2>
              </div>
            </Reveal>

            <Reveal delay={300} data-id="element-178">
              <div className="relative overflow-hidden rounded-2xl bg-white/40 backdrop-blur-xl border border-white/60 shadow-[0_8px_32px_rgba(73,111,88,0.05)] p-6 md:p-8" data-id="element-179">
                <div className="font-sans-brand text-sm md:text-[15px] text-[#2F3E34] font-medium leading-relaxed space-y-5 text-left" data-id="element-180">
                  <p data-id="element-181">
                    Rooted in Scripture, our mission is to combine{' '}
                    <strong className="font-semibold text-[#496f58]" data-id="element-182">
                      biblical ingredients, wellness, and modern research
                    </strong>{' '}
                    to help individuals care for their bodies as temples of the
                    Holy Spirit.
                  </p>
                  <div className="w-10 h-px bg-[#496f58]/20" data-id="element-183"></div>
                  <p data-id="element-184">
                    By uniting trusted biblical botanicals with clinically
                    informed research, Faith & Fig offers clean, effective
                    support.
                  </p>
                </div>
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" data-id="element-185"></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 3: IMAGE TEXT SLIDESHOW */}
      <ImageTextSlideshow data-id="element-186" />

      {/* SECTION 4: OUR PHILOSOPHY */}
      <section className="relative bg-[#F9F7F2] py-20 lg:py-32 overflow-hidden" data-id="element-187">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-[#496f58] rounded-full mix-blend-multiply filter blur-3xl opacity-5 pointer-events-none" data-id="element-188"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-[#C9A962] rounded-full mix-blend-multiply filter blur-3xl opacity-5 pointer-events-none" data-id="element-189"></div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" data-id="element-190">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start" data-id="element-191">
            <div className="lg:col-span-7 space-y-8" data-id="element-192">
              <Reveal delay={0} data-id="element-193">
                <div data-id="element-194">
                  <span className="font-sans-brand text-[#C9A962] tracking-widest text-sm font-bold uppercase mb-3 block" data-id="element-195">
                    Our Philosophy
                  </span>
                  <h2 className="font-serif-brand text-3xl md:text-4xl lg:text-5xl text-[#496f58] leading-tight" data-id="element-196">
                    God has provided every plant, herb, and seed needed to care
                    for the human body.
                  </h2>
                </div>
              </Reveal>

              <Reveal delay={200} data-id="element-197">
                <div className="prose prose-lg text-gray-600 font-sans-brand leading-relaxed" data-id="element-198">
                  <p data-id="element-199">
                    Our mission is to honor these biblical ingredients by
                    combining them with modern research, bringing thoughtful,
                    creation-based support to the body He entrusted to us.
                  </p>
                  <p data-id="element-200">
                    By uniting{' '}
                    <span className="text-[#496f58] font-semibold" data-id="element-201">
                      biblical wisdom
                    </span>{' '}
                    with{' '}
                    <span className="text-[#496f58] font-semibold" data-id="element-202">
                      evidence-based insight
                    </span>
                    , we steward His provisions with care, integrity, and
                    excellence.
                  </p>
                  <blockquote className="border-l-4 border-[#C9A962] pl-6 py-2 my-8 italic text-gray-500" data-id="element-203">
                    "We do not reinvent what God has made; we simply use it as
                    He intended."
                  </blockquote>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5" data-id="element-204">
              <Reveal delay={400} data-id="element-205">
                <div className="bg-white p-8 md:p-10 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#496f58]/10" data-id="element-206">
                  <h4 className="text-2xl font-serif-brand text-[#496f58] mb-8 pb-4 border-b border-gray-100 flex items-center gap-3" data-id="element-207">
                    <span data-id="element-208">Our Promise to You</span>
                  </h4>
                  <div className="space-y-8" data-id="element-209">
                    {/* Staggered Features */}
                    {[{
                    title: 'Biblical Ingredients First',
                    desc: 'Honoring the plants and botanicals God created with intention.',
                    path: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                  }, {
                    title: 'Rigorous Sourcing',
                    desc: 'Guiding every formulation and purpose with strict standards.',
                    path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                  }, {
                    title: 'Purity & Integrity',
                    desc: 'No fillers, no shortcuts, no compromise.',
                    path: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z'
                  }, {
                    title: 'Stewardship',
                    desc: "Using research to better understand God's provisions.",
                    path: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                  }].map((feature, i) => <Reveal key={i} delay={500 + i * 100} data-id="element-210">
                        <div className="flex gap-4 group" data-id="element-211">
                          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#F3F6F4] flex items-center justify-center group-hover:bg-[#496f58] transition-colors duration-300" data-id="element-212">
                            <svg className="w-6 h-6 text-[#496f58] group-hover:text-white transition-colors duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-213">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={feature.path} data-id="element-214" />
                            </svg>
                          </div>
                          <div data-id="element-215">
                            <h5 className="font-serif-brand font-semibold text-gray-900 mb-1" data-id="element-216">
                              {feature.title}
                            </h5>
                            <p className="font-sans-brand text-sm text-gray-500 leading-relaxed" data-id="element-217">
                              {feature.desc}
                            </p>
                          </div>
                        </div>
                      </Reveal>)}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>;
}
// --- FOOTER COMPONENT ---
export function Footer() {
  return <footer className="bg-[#496f58] text-white font-sans-brand pt-12" data-id="element-218">
      <Reveal data-id="element-219">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-8" data-id="element-220">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-b border-white/20 pb-10" data-id="element-221">
            {/* Company Column */}
            <div data-id="element-222">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-223">
                Faith & Fig
              </h4>
              <ul className="space-y-2" data-id="element-224">
                <li data-id="element-225">
                  <a href="https://supplement.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-226">
                    Our Mission
                  </a>
                </li>
                <li data-id="element-227">
                  <a href="https://faithandfig.com/pages/contact" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-228">
                    Contact
                  </a>
                </li>
                {/* UPGRADED POLICIES DROPDOWN */}
                <li data-id="element-229">
                  <div className="relative group" data-id="element-230">
                    <button className="text-white/80 hover:text-white transition-colors text-sm flex items-center gap-1 pb-1" data-id="element-231">
                      Policies
                      <ChevronDownIcon size={10} className="transition-transform duration-200 group-hover:rotate-180" data-id="element-232" />
                    </button>

                    {/* Dropdown Menu - Expands Upwards */}
                    <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-bottom translate-y-2 group-hover:translate-y-0 z-50" data-id="element-233">
                      <a href="https://faithandfig.com/policies/privacy-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-234">
                        Privacy Policy
                      </a>
                      <a href="https://faithandfig.com/policies/refund-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-235">
                        Refund Policy
                      </a>
                      <a href="https://faithandfig.com/policies/shipping-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-236">
                        Shipping Policy
                      </a>
                      <a href="https://faithandfig.com/policies/terms-of-service" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-237">
                        Terms of Service
                      </a>
                      {/* Tiny triangle pointer at bottom */}
                      <div className="absolute -bottom-1 left-4 w-2 h-2 bg-white transform rotate-45" data-id="element-238"></div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            {/* Shop Column */}
            <div data-id="element-239">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-240">Shop</h4>
              <ul className="space-y-2" data-id="element-241">
                <li data-id="element-242">
                  <a href="https://pomegranates.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-243">
                    Heart Health
                  </a>
                </li>
                <li data-id="element-244">
                  <a href="https://oliveleaf.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-245">
                    Immune Defense
                  </a>
                </li>
                <li data-id="element-246">
                  <a href="https://frankincense.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-247">
                    Joint Comfort
                  </a>
                </li>
                <li data-id="element-248">
                  <a href="https://trysaffron.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-249">
                    Mood & Focus
                  </a>
                </li>
              </ul>
            </div>
            {/* Account Column */}
            <div data-id="element-250">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-251">Account</h4>
              <ul className="space-y-2" data-id="element-252">
                <li data-id="element-253">
                  <a href="https://faithandfig.com/apps/my-subscriptions" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-254">
                    Manage Subscriptions
                  </a>
                </li>
                <li data-id="element-255">
                  <a href="https://faithandfig.com/apps/track123" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-256">
                    Track Order
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6" data-id="element-257">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/70" data-id="element-258">
            <p data-id="element-259">
              © {new Date().getFullYear()} Faith & Fig. All rights reserved.
            </p>
          </div>
        </div>
      </Reveal>
    </footer>;
}
// --- MAIN ABOUT US PAGE COMPONENT ---
export function AboutUsPage() {
  return <>
      <GoogleTagManager data-id="element-260" />
      <MetaPixel data-id="element-261" />
      <Navbar data-id="element-262" />
      <main className="pt-[70px] flex flex-col min-h-screen" data-id="element-263">
        <FaithAndFigCampaign data-id="element-264" />
        <AboutUsSections data-id="element-265" />
        <NoteFromFounder data-id="element-266" />
      </main>
      <Footer data-id="element-267" />
    </>;
}
export default ImageTextSlideshow;